// The Sent history tab's data: past sends, and pulling a live one back.

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import type { AnnouncementBatch, BannerTab } from './bannerOptions';
import type { PushLogEntry } from './pushLog';

export type StatusFilter = 'all' | 'live' | 'ended' | 'removed';

export function useSentHistory({
  open,
  report,
}: {
  /**
   * The list is on screen. Every opening reads the list again: a send just made shows, and
   * coming back from a tracker, which works out each person's state and writes it back, shows
   * what the tracker said.
   */
  open: boolean;
  report: (entry: PushLogEntry, from: BannerTab) => void;
}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const [history, setHistory] = useState<AnnouncementBatch[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // A list that could not be read must not read as nothing sent.
  const [historyError, setHistoryError] = useState<string | null>(null);
  const lastRead = useRef(0);
  const [revokingBatch, setRevokingBatch] = useState<string | null>(null);
  // What the list is narrowed to. Here rather than in the tab, so coming back from
  // another tab finds it as it was left, like the rest of the page.
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [type, setType] = useState('all');

  const fetchHistory = useCallback(async () => {
    if (selectedProjectId == null) return;
    const thisRead = ++lastRead.current;
    setHistoryLoading(true);
    try {
      const res = await api.get('/announcements/history', {
        params: { projectId: selectedProjectId, limit: 50 },
      });
      if (thisRead !== lastRead.current) return;
      setHistory(res.data?.data ?? []);
      setHistoryError(null);
    } catch (err: any) {
      // A failed load keeps what is on screen, and says so.
      if (thisRead === lastRead.current) setHistoryError(err?.response?.data?.message || 'Could not load Sent history.');
    } finally {
      if (thisRead === lastRead.current) setHistoryLoading(false);
    }
  }, [selectedProjectId]);

  // Another project's sends are not this one's, whether or not the next read works.
  useEffect(() => {
    setHistory([]);
    setHistoryError(null);
  }, [selectedProjectId]);

  // One list for both kinds — the endpoint returns messages and tasks together.
  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  /**
   * Pull a sent banner back. This is the only undo — the mobile card has no
   * dismiss control, so until this runs the recipients are stuck with it.
   */
  const revokeBatch = async (b: Pick<AnnouncementBatch, 'batchId' | 'title' | 'recipients' | 'kind' | 'taskType'>) => {
    const ok = window.confirm(
      `Remove "${b.title}" from ${b.recipients} employee${b.recipients === 1 ? '' : 's'}?\n\n` +
        `It disappears from their home screen straight away. This cannot be undone — ` +
        `you would have to send it again.`
    );
    if (!ok) return;
    setRevokingBatch(b.batchId);
    try {
      const res = await api.post(`/announcements/history/${b.batchId}/revoke`);
      report(
        {
          timestamp: new Date().toLocaleTimeString(),
          empCode: `${b.recipients} recipient${b.recipients === 1 ? '' : 's'}`,
          type: b.kind === 'task' ? 'task' : 'personalized',
          taskType: b.kind === 'task' ? b.taskType ?? null : undefined,
          success: true,
          message: `Removed "${b.title}" — ${res.data?.message ?? 'done'}`,
        },
        'history'
      );
      await fetchHistory();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to remove the banner');
    } finally {
      setRevokingBatch(null);
    }
  };

  return {
    history,
    historyLoading,
    historyError,
    fetchHistory,
    revokingBatch,
    revokeBatch,
    query,
    setQuery,
    status,
    setStatus,
    type,
    setType,
  };
}

export type SentHistory = ReturnType<typeof useSentHistory>;
